import { useState, useEffect } from "react";
import { getFarmers, getMilkEntries, getCattleFoodEntries, getTransactions, deleteTransaction, addFarmerPayment, getFarmerPayments, getTotalPaymentsForFarmer, deleteFarmerPayment } from "../services/firebaseService";
import { FaCalendarAlt, FaFilter, FaMoneyBillWave, FaUsers, FaChartLine, FaHistory, FaLeaf, FaSun, FaMoon, FaTrash, FaSave } from "react-icons/fa";

export default function Bill() {
  const [entries, setEntries] = useState([]);
  const [cattleFoodEntries, setCattleFoodEntries] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [fwFromDate, setFwFromDate] = useState("");
  const [fwToDate, setFwToDate] = useState("");
  const [fwFiltered, setFwFiltered] = useState(entries);
  const [fwCattleFoodFiltered, setFwCattleFoodFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [givenAmounts, setGivenAmounts] = useState({});
  const [savingPayment, setSavingPayment] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [farmersData, entriesData, cattleFoodData, transactionsData, paymentsData] = await Promise.all([
        getFarmers(), getMilkEntries(), getCattleFoodEntries(), getTransactions(), getFarmerPayments()
      ]);
      setFarmers(farmersData);
      setEntries(entriesData);
      setCattleFoodEntries(cattleFoodData);
      setTransactions(transactionsData);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGivenAmountChange = (farmerCode, value) => {
    setGivenAmounts(prev => ({ ...prev, [farmerCode]: value }));
  };

  const handleSavePayment = async (farmer) => {
    const amount = parseFloat(givenAmounts[farmer.code] || 0);
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    setSavingPayment(prev => ({ ...prev, [farmer.code]: true }));
    try {
      await addFarmerPayment({
        farmerCode: farmer.code,
        farmerName: farmer.name,
        amount,
        timestamp: new Date().toISOString()
      });
      
      // Refresh payments
      const updatedPayments = await getFarmerPayments(false);
      setPayments(updatedPayments);
      
      // Clear input
      setGivenAmounts(prev => ({ ...prev, [farmer.code]: '' }));
      alert(`₹${amount} saved successfully for ${farmer.name}`);
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Error saving payment. Please try again.');
    } finally {
      setSavingPayment(prev => ({ ...prev, [farmer.code]: false }));
    }
  };

  const handleFarmerWiseFilter = () => {
    if (!fwFromDate || !fwToDate || fwFromDate > fwToDate) return;
    setFwFiltered(entries.filter(e => e.date >= fwFromDate && e.date <= fwToDate));
    setFwCattleFoodFiltered(cattleFoodEntries.filter(e => e.date >= fwFromDate && e.date <= fwToDate));
  };

  const clearFilter = () => { setFwFromDate(""); setFwToDate(""); setFwFiltered(entries); setFwCattleFoodFiltered(cattleFoodEntries); };

  useEffect(() => { if (!fwFromDate || !fwToDate) { setFwFiltered(entries); setFwCattleFoodFiltered(cattleFoodEntries); } }, [entries, cattleFoodEntries, fwFromDate, fwToDate]);

  const recentTransactions = transactions.slice(0, 10);
  const totalMilkAmount = farmers.reduce((sum, farmer) => {
    const farmerEntries = fwFiltered.filter(e => e.farmerCode === farmer.code);
    const sumAmt = (animal, time) => farmerEntries.filter(e => e.animalType === animal && e.time === time).reduce((s, e) => s + (e.quantity * e.price || 0), 0);
    return sum + sumAmt("Cow", "Morning") + sumAmt("Cow", "Evening") + sumAmt("Buffalo", "Morning") + sumAmt("Buffalo", "Evening");
  }, 0);
  const totalCattleFood = farmers.reduce((sum, farmer) => {
    const farmerCattleFoodEntries = fwCattleFoodFiltered.filter(e => e.farmerCode === farmer.code);
    return sum + farmerCattleFoodEntries.reduce((s, entry) => s + entry.totalAmount, 0);
  }, 0);

  return (
    <div className="container fade-in">
      <div className="bill-stats-row">
        <div className="bill-stat-card"><div className="bill-stat-icon" style={{ background: 'linear-gradient(135deg, #4caf50, #66bb6a)' }}><FaMoneyBillWave /></div><div className="bill-stat-info"><span className="bill-stat-value">₹{totalMilkAmount.toFixed(0)}</span><span className="bill-stat-label">Total Milk Amount</span></div></div>
        <div className="bill-stat-card"><div className="bill-stat-icon" style={{ background: 'linear-gradient(135deg, #ff9800, #ffb74d)' }}><FaLeaf /></div><div className="bill-stat-info"><span className="bill-stat-value">₹{totalCattleFood.toFixed(0)}</span><span className="bill-stat-label">Cattle Food</span></div></div>
        <div className="bill-stat-card"><div className="bill-stat-icon" style={{ background: 'linear-gradient(135deg, #2196f3, #64b5f6)' }}><FaUsers /></div><div className="bill-stat-info"><span className="bill-stat-value">{farmers.length}</span><span className="bill-stat-label">Total Farmers</span></div></div>
        <div className="bill-stat-card"><div className="bill-stat-icon" style={{ background: 'linear-gradient(135deg, #9c27b0, #ba68c8)' }}><FaHistory /></div><div className="bill-stat-info"><span className="bill-stat-value">{payments.length}</span><span className="bill-stat-label">Payments</span></div></div>
      </div>

      <div className="card bill-card">
        <div className="bill-card-header"><h3><FaFilter className="header-icon" /> Filter by Date Range</h3></div>
        <div className="bill-filter-section">
          <div className="filter-group"><label className="filter-label"><FaCalendarAlt /> From Date</label><input type="date" value={fwFromDate} onChange={e => setFwFromDate(e.target.value)} className="form-input" /></div>
          <div className="filter-group"><label className="filter-label"><FaCalendarAlt /> To Date</label><input type="date" value={fwToDate} onChange={e => setFwToDate(e.target.value)} className="form-input" /></div>
          <div className="filter-actions">
            <button className="btn btn-primary" onClick={handleFarmerWiseFilter} disabled={!fwFromDate || !fwToDate || fwFromDate > fwToDate}><FaFilter /> Apply Filter</button>
            {(fwFromDate || fwToDate) && (<button className="btn btn-secondary" onClick={clearFilter}>Clear</button>)}
          </div>
        </div>
      </div>

      <div className="card bill-card">
        <div className="bill-card-header"><h3><FaChartLine className="header-icon" /> Farmer-wise Summary</h3>{fwFromDate && fwToDate && (<span className="date-range-badge">{fwFromDate} to {fwToDate}</span>)}</div>
        <div className="table-container">
          <table className="table bill-table">
            <thead><tr><th>Farmer</th><th><FaSun /> Cow AM</th><th><FaMoon /> Cow PM</th><th><FaSun /> Buffalo AM</th><th><FaMoon /> Buffalo PM</th><th><FaLeaf /> Cattle Food</th><th>Grand Total</th><th>Given Amount</th><th>Remaining</th></tr></thead>
            <tbody>
              {farmers.length === 0 ? (<tr><td colSpan="9" className="empty-state">No farmers registered.</td></tr>) : (
                farmers.map((farmer, idx) => {
                  const farmerEntries = fwFiltered.filter(e => e.farmerCode === farmer.code);
                  const farmerCattleFoodEntries = fwCattleFoodFiltered.filter(e => e.farmerCode === farmer.code);
                  const sumQty = (animal, time) => farmerEntries.filter(e => e.animalType === animal && e.time === time).reduce((sum, e) => sum + (e.quantity || 0), 0);
                  const sumAmt = (animal, time) => farmerEntries.filter(e => e.animalType === animal && e.time === time).reduce((sum, e) => sum + (e.quantity * e.price || 0), 0);
                  const cowMorningQty = sumQty("Cow", "Morning"), cowMorningAmt = sumAmt("Cow", "Morning");
                  const cowEveningQty = sumQty("Cow", "Evening"), cowEveningAmt = sumAmt("Cow", "Evening");
                  const buffaloMorningQty = sumQty("Buffalo", "Morning"), buffaloMorningAmt = sumAmt("Buffalo", "Morning");
                  const buffaloEveningQty = sumQty("Buffalo", "Evening"), buffaloEveningAmt = sumAmt("Buffalo", "Evening");
                  const cattleFoodTotal = farmerCattleFoodEntries.reduce((sum, entry) => sum + entry.totalAmount, 0);
                  const totalQty = cowMorningQty + cowEveningQty + buffaloMorningQty + buffaloEveningQty;
                  const totalMilkAmt = cowMorningAmt + cowEveningAmt + buffaloMorningAmt + buffaloEveningAmt;
                  const totalGiven = getTotalPaymentsForFarmer(payments, farmer.code);
                  const remaining = totalMilkAmt - cattleFoodTotal - totalGiven;
                  return (
                    <tr key={idx} className="farmer-row">
                      <td><div className="farmer-cell"><div className="farmer-avatar-small">{farmer.name.charAt(0).toUpperCase()}</div><div className="farmer-cell-info"><span className="farmer-name">{farmer.name}</span><span className="farmer-code">{farmer.code}</span></div></div></td>
                      <td className="milk-cell"><span className="qty">{cowMorningQty}L</span><span className="amt">₹{cowMorningAmt.toFixed(0)}</span></td>
                      <td className="milk-cell"><span className="qty">{cowEveningQty}L</span><span className="amt">₹{cowEveningAmt.toFixed(0)}</span></td>
                      <td className="milk-cell"><span className="qty">{buffaloMorningQty}L</span><span className="amt">₹{buffaloMorningAmt.toFixed(0)}</span></td>
                      <td className="milk-cell"><span className="qty">{buffaloEveningQty}L</span><span className="amt">₹{buffaloEveningAmt.toFixed(0)}</span></td>
                      <td className="cattle-food-cell">₹{cattleFoodTotal.toFixed(0)}</td>
                      <td className="total-cell"><span className="total-qty">{totalQty}L</span><span className="total-amt">₹{totalMilkAmt.toFixed(0)}</span></td>
                      <td className="given-amount-cell">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {totalGiven > 0 && <span style={{ fontSize: '12px', color: '#2196f3' }}>Paid: ₹{totalGiven.toFixed(0)}</span>}
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                              type="number"
                              placeholder="Amount"
                              value={givenAmounts[farmer.code] || ''}
                              onChange={(e) => handleGivenAmountChange(farmer.code, e.target.value)}
                              style={{ width: '80px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' }}
                            />
                            <button
                              onClick={() => handleSavePayment(farmer)}
                              disabled={savingPayment[farmer.code]}
                              style={{ padding: '4px 8px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FaSave /> {savingPayment[farmer.code] ? '...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className={`remaining-cell ${remaining < 0 ? 'negative' : 'positive'}`}>₹{remaining.toFixed(0)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>



      <div className="card bill-card">
        <div className="bill-card-header"><h3><FaHistory className="header-icon" /> Recent Payments (Given Amounts)</h3></div>
        {payments.length === 0 ? (<div className="empty-state"><FaHistory className="empty-state-icon" /><p>No payments recorded yet.</p></div>) : (
          <div className="transactions-grid">
            {payments.slice(0, 20).map((payment) => (
              <div key={payment.id} className="transaction-card fade-in">
                <div className="transaction-header">
                  <div className="transaction-farmer">
                    <div className="transaction-avatar">{payment.farmerName?.charAt(0).toUpperCase() || '?'}</div>
                    <div className="transaction-farmer-info">
                      <span className="transaction-farmer-name">{payment.farmerName}</span>
                      <span className="transaction-code">{payment.farmerCode}</span>
                    </div>
                  </div>
                </div>
                <div className="transaction-details" style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <div className="transaction-detail" style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                    <span className="detail-label">Amount Given:</span>
                    <span className="detail-value" style={{ fontWeight: 'bold', color: '#2196f3', fontSize: '18px' }}>₹{payment.amount?.toFixed(0) || '0'}</span>
                  </div>
                  <div className="transaction-detail" style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                    <span>{new Date(payment.timestamp || payment.createdAt?.toDate?.() || payment.createdAt).toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <button 
                  className="btn btn-danger btn-sm" 
                  style={{ marginTop: '10px', width: '100%' }}
                  onClick={async () => {
                    if (window.confirm(`Delete payment of ₹${payment.amount} for ${payment.farmerName}?`)) {
                      try {
                        await deleteFarmerPayment(payment.id);
                        const updatedPayments = await getFarmerPayments(false);
                        setPayments(updatedPayments);
                      } catch (error) {
                        console.error('Error deleting payment:', error);
                        alert('Error deleting payment');
                      }
                    }
                  }}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
